import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, Crown } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';

import { useAuth } from '../context/AuthContext';
import FogBackground from '../components/FogBackground';

const Landing: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, signInGuest, loading } = useAuth();
    const [verifying, setVerifying] = useState(false);

    const returnUrl = location.state?.returnUrl;
    const isJoinAction = location.state?.action === 'join';

    // Dummy Site Key for Testing (Always Pass)
    // Replace with your real Cloudflare Turnstile Site Key in Production
    const SITE_KEY = "0x4AAAAAACJWuQcISY1xgSXR";

    const handleTurnstileSuccess = async (token: string) => {
        setVerifying(true);
        try {
            await signInGuest(token);
            navigate(returnUrl || '/court', { replace: true });
        } catch (error) {
            console.error("Guest sign-in error", error);
            setVerifying(false);
        }
    };

    return (
        <div className="min-h-screen bg-tudor-black relative overflow-hidden flex flex-col items-center justify-center text-center p-6" style={{ backgroundImage: "url('/assets/landing-bg.webp')", backgroundSize: 'cover', backgroundPosition: 'center top' }}>
            {/* Fog Effects */}
            {/* Fog Effects (GLSL Shader) */}
            <FogBackground />

            {/* Dark Overlay with Fade Up */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/20 pointer-events-none"></div>
            {/* Strong Bottom Fade */}
            <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-black to-transparent pointer-events-none"></div>
            {/* Strong Top Fade */}
            <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-black to-transparent pointer-events-none"></div>

            <div className="relative z-10 bottom-45 max-w-4xl mx-auto space-y-10 animate-fade-in-up drop-shadow-[0_10px_10px_rgba(0,0,0,0.8)]">
                {/* Header/Logo Area */}
                <div className="space-y-2 flex flex-col items-center">
                    <img src="./logo.png" style={{ width: '108px', opacity: '0.85' }} />
                    <h1 className="flex flex-col items-center font-serif text-tudor-gold tracking-[0.15em] leading-none text-shadow-lg">
                        <span className="text-4xl md:text-6xl font-medium opacity-90">TUDOR</span>
                        <span className="text-6xl md:text-8xl mt-1 tracking-[0.1em]">CHESS</span>
                    </h1>
                    <div className="h-px w-24 bg-tudor-gold/60 mt-6 md:mt-8"></div>
                </div>

                {/* Killer Quote */}
                <blockquote className="py-2">
                    <p className="text-2xl md:text-4xl font-serif text-zinc-300 italic tracking-wider">
                        Lose the battle <span className="text-tudor-gold">win the war</span>
                    </p>
                </blockquote>

                {/* Call to Action / Guest Entry */}
                <div className="pt-4 min-h-[100px] flex flex-col items-center justify-center">
                    {loading ? (
                        <div className="flex flex-col items-center gap-3 animate-pulse text-tudor-gold">
                            <div className="w-6 h-6 border-2 border-tudor-gold border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-xs uppercase tracking-widest">Initializing Royal Protocol...</span>
                        </div>
                    ) : !user ? (
                        <>
                            {!verifying ? (
                                <button
                                    onClick={() => setVerifying(true)}
                                    className="group relative inline-flex items-center gap-4 px-10 py-3 bg-transparent border border-tudor-gold text-tudor-gold font-bold tracking-[0.25em] text-xs md:text-sm uppercase hover:bg-tudor-gold/10 transition-all duration-300"
                                >
                                    <span className="relative z-10">{isJoinAction ? 'DEPLOY TO BATTLE' : 'ENTER THE COURT'}</span>
                                    <ChevronRight className="w-4 h-4 relative z-10 group-hover:translate-x-1 transition-transform" />
                                </button>
                            ) : (
                                <div className="space-y-4 animate-fade-in">
                                    <div className="text-tudor-gray/60 text-xs tracking-widest uppercase mb-2">
                                        Security Check Protocol
                                    </div>
                                    <Turnstile
                                        siteKey={SITE_KEY}
                                        onSuccess={handleTurnstileSuccess}
                                        options={{
                                            action: 'guest-login',
                                            theme: 'dark'
                                        }}
                                    />
                                    <button
                                        onClick={() => setVerifying(false)}
                                        className="text-xs text-tudor-red hover:text-red-400 underline decoration-dotted underline-offset-4 mt-2"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <button
                            onClick={() => navigate(returnUrl || '/court')}
                            className="group relative inline-flex items-center gap-4 px-10 py-3 bg-transparent border border-tudor-gold text-tudor-gold font-bold tracking-[0.25em] text-xs md:text-sm uppercase hover:bg-tudor-gold/10 transition-all duration-300"
                        >
                            <span className="relative z-10">{isJoinAction ? 'DEPLOY TO BATTLE' : 'ENTER THE COURT'}</span>
                            <ChevronRight className="w-4 h-4 relative z-10 group-hover:translate-x-1 transition-transform" />
                        </button>
                    )}
                </div>
            </div>

            {/* Footer / Credits */}
            <div className="absolute bottom-8 text-tudor-gray/40 text-xs tracking-widest uppercase">
                System Version 0.9 // Royal Protocol Active
            </div>
        </div>
    );
};


export default Landing;
